/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DmsMetadataService } from '../upload/dms-metadata.service';
import { WorkflowSettingsService } from '../workflow/workflow-settings.service';
import { Bucket, DocumentInsights, InsightsService, MonthCount, OpenFlow, WorkflowInsights } from './insights.service';

/** One field's breakdown, and the all-documents filter a row opens — none when that screen cannot filter by it. */
interface Breakdown {
  key: 'status' | 'department' | 'type' | 'level';
  titleKey: string;
  icon: string;
  filter?: 'status' | 'department' | 'documentType';
  /** Values are model codes with a Thai name, not free text. */
  coded: boolean;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** Engine definitions a flow started without a template runs, by their readable names. */
const FLOW_NAMES: Record<string, string> = {
  activitiReview: 'DMS.INSIGHTS.FLOW_REVIEW',
  activitiReviewPooled: 'DMS.INSIGHTS.FLOW_REVIEW_POOLED',
  activitiParallelReview: 'DMS.INSIGHTS.FLOW_PARALLEL',
  activitiParallelGroupReview: 'DMS.INSIGHTS.FLOW_PARALLEL_GROUP',
  activitiAdhoc: 'DMS.INSIGHTS.FLOW_ADHOC'
};

/**
 * Organisation overview — the page an executive opens: what is in the
 * repository, by status, department, type and level; how much arrives each
 * month; and how long approvals take, with the ones that have waited longest.
 *
 * The figures are the reader's: counted over what they may read, so the page
 * is safe to open to anyone, and complete for an administrator.
 */
@Component({
  selector: 'app-insights-page',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './insights-page.component.html',
  styleUrls: ['./insights-page.component.scss']
})
export class InsightsPageComponent implements OnInit {
  private readonly insights = inject(InsightsService);
  private readonly dms = inject(DmsMetadataService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  readonly workflowSettings = inject(WorkflowSettingsService);

  readonly loading = signal(true);
  readonly failed = signal(false);
  readonly docs = signal<DocumentInsights | null>(null);
  readonly months = signal<MonthCount[]>([]);
  /** Null while loading, and when the workflow engine could not be asked. */
  readonly flows = signal<WorkflowInsights | null>(null);
  readonly now = signal(new Date());

  readonly breakdowns: Breakdown[] = [
    { key: 'status', titleKey: 'DMS.INSIGHTS.BY_STATUS', icon: 'flag', filter: 'status', coded: true },
    { key: 'department', titleKey: 'DMS.INSIGHTS.BY_DEPARTMENT', icon: 'apartment', filter: 'department', coded: false },
    { key: 'type', titleKey: 'DMS.INSIGHTS.BY_TYPE', icon: 'category', filter: 'documentType', coded: true },
    { key: 'level', titleKey: 'DMS.INSIGHTS.BY_LEVEL', icon: 'shield', coded: true }
  ];

  readonly thisMonth = computed(() => this.months().at(-1)?.count ?? 0);
  readonly monthPeak = computed(() => Math.max(1, ...this.months().map((m) => m.count)));
  readonly yearTotal = computed(() => this.months().reduce((sum, m) => sum + m.count, 0));

  ngOnInit(): void {
    // the type names added from the settings screen arrive with the schema
    this.dms
      .schema()
      .pipe(catchError(() => of(null)))
      .subscribe(() => this.reload());
  }

  reload(): void {
    this.loading.set(true);
    this.failed.set(false);
    this.now.set(new Date());
    forkJoin({
      docs: this.insights.documents().pipe(catchError(() => of(null))),
      months: this.insights.uploadsByMonth(this.now()).pipe(catchError(() => of(null))),
      flows: this.insights.workflows(this.now()).pipe(catchError(() => of(null)))
    }).subscribe(({ docs, months, flows }) => {
      this.docs.set(docs);
      this.months.set(months ?? []);
      this.flows.set(flows);
      this.failed.set(!docs && !months);
      this.loading.set(false);
    });
  }

  buckets(key: Breakdown['key']): Bucket[] {
    const docs = this.docs();
    if (!docs) {
      return [];
    }
    return { status: docs.byStatus, department: docs.byDepartment, type: docs.byType, level: docs.byLevel }[key];
  }

  /** Bar length against the largest value in its own card. */
  share(bucket: Bucket, all: Bucket[]): number {
    const peak = Math.max(1, ...all.map((b) => b.count));
    return Math.round((bucket.count / peak) * 100);
  }

  percent(bucket: Bucket): number {
    const total = this.docs()?.total ?? 0;
    return total ? Math.round((bucket.count / total) * 100) : 0;
  }

  /** What a value is called: its Thai name for a model code, the text itself otherwise. */
  label(bucket: Bucket, breakdown: Breakdown): string {
    if (!bucket.value) {
      return 'DMS.INSIGHTS.NOT_SET';
    }
    return breakdown.coded ? DmsMetadataService.label(bucket.value) : bucket.value;
  }

  /** The documents behind a row, on the all-documents screen. A "not set" row cannot be filtered there. */
  open(bucket: Bucket, breakdown: Breakdown): void {
    if (!breakdown.filter || !bucket.value) {
      return;
    }
    void this.router.navigate(['/all-documents'], { queryParams: { [breakdown.filter]: bucket.value } });
  }

  columnHeight(month: MonthCount): number {
    return Math.round((month.count / this.monthPeak()) * 100);
  }

  /** The first of the month, for the date pipe. */
  monthDate(month: MonthCount): Date {
    const [year, mon] = month.month.split('-').map(Number);
    return new Date(year, mon - 1, 1);
  }

  flowName(name: string, builtin: boolean): string {
    if (!builtin) {
      return name;
    }
    return FLOW_NAMES[name] ?? name.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  /** A duration the way a person says it: minutes, hours, or days to one decimal. */
  duration(ms: number | null): string {
    if (ms === null || ms === undefined) {
      return '—';
    }
    if (ms < HOUR) {
      return this.translate.instant('DMS.INSIGHTS.MINUTES', { p0: Math.max(1, Math.round(ms / 60_000)) });
    }
    if (ms < DAY) {
      return this.translate.instant('DMS.INSIGHTS.HOURS', { p0: Math.round(ms / HOUR) });
    }
    return this.translate.instant('DMS.INSIGHTS.DAYS', { p0: Math.round((ms / DAY) * 10) / 10 });
  }

  age(flow: OpenFlow): string {
    return this.duration(this.now().getTime() - (Date.parse(flow.startedAt) || this.now().getTime()));
  }

  openFlow(flow: OpenFlow): void {
    if (flow.processId) {
      void this.router.navigate(['/workflows/instances', flow.processId]);
    }
  }
}
